import React from "react";
import {
  Select,
  MenuItem,
  Typography,
  Box,
  Divider,
  SelectChangeEvent,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AdvancedDdlResult } from "../../../../services/api/types/users/users";

interface GroupedSelectProps {
  value: string;
  onChange: (value: string, groupHeader?: string, filterGroup?: string) => void;
  data: AdvancedDdlResult[];
  disabled?: boolean;
  usedAttributes?: string[];
  sx?: any;
  size?: "small" | "medium";
  error?: boolean;
}

export const GroupedSelect: React.FC<GroupedSelectProps> = ({
  value,
  onChange,
  data,
  disabled = false,
  usedAttributes = [],
  sx = {},
  size = "medium",
  error = false,
}) => {
  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value;
    let groupHeader: string | undefined;
    let filterGroup: string | undefined;
    for (const group of data) {
      const foundItem = group.items.find(
        (item) => item.attributeKey === selectedValue
      );
      if (foundItem) {
        groupHeader = group.groupHeader;
        filterGroup = foundItem.filterGroup;
        break;
      }
    }

    onChange(selectedValue, groupHeader, filterGroup);
  };

  const getSelectedDisplayName = () => {
    if (!value) return "Select attribute...";

    for (const group of data) {
      const foundItem = group.items.find((item) => item.attributeKey === value);
      if (foundItem) {
        return foundItem.attributeName;
      }
    }
    return value;
  };

  const defaultSx = {
    borderRadius: "12px",
    minWidth: "150px",
    width: "100%",
    maxWidth: "100%",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      padding: "8.5px 14px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  };

  return (
    <Select
      value={value}
      onChange={handleChange}
      IconComponent={ExpandMoreIcon}
      sx={{ ...defaultSx, ...sx }}
      size={size}
      disabled={disabled}
      error={error}
      renderValue={(selected) => (
        <Typography
          variant={selected ? "body2" : "caption"}
          sx={{
            color: selected ? "text.primary" : "text.secondary",
            fontStyle: selected ? "normal" : "italic",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
          }}
        >
          {getSelectedDisplayName()}
        </Typography>
      )}
    >
      {data
        .map((group, groupIndex) => [
          <MenuItem
            key={`header-${groupIndex}`}
            disabled
            sx={{
              backgroundColor: "#f5f5f5",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
              cursor: "default",
              opacity: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                fontSize: "0.875rem",
              }}
            >
              {group.groupHeader}
            </Typography>
          </MenuItem>,
          ...group.items.map((item, itemIndex) => (
            <MenuItem
              key={`${groupIndex}-${itemIndex}`}
              value={item.attributeKey}
              disabled={
                usedAttributes.includes(item.attributeKey) &&
                item.attributeKey !== value
              }
              sx={{
                pl: 3,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {item.attributeName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                >
                  {item.dataType}
                </Typography>
              </Box>
            </MenuItem>
          )),
          ...(groupIndex < data.length - 1
            ? [<Divider key={`divider-${groupIndex}`} sx={{ my: 0.5 }} />]
            : []),
        ])
        .flat()}
    </Select>
  );
};
